import { handleApiError } from '@api';
import config from '@config';
import { HTTPClient } from '@platform2-lib/httpz';
import * as schema from '@schema';
import type {
  ArtifactDetailResponse,
  ArtifactListItem,
  ArtifactVersion,
  ArtifactVersionsResponse
} from 'types/schema';

type ArtifactContentType = 'json' | 'markdown';

const client = new HTTPClient(
  config.env,
  `${config.api.origin}/spark-agent-v2`
);

async function getAll(): Promise<ArtifactListItem[]> {
  try {
    return await client.get('/artifacts', schema.ArtifactListResponse);
  } catch (error: unknown) {
    handleApiError(error, 'Unable to retrieve artifacts.');

    return [];
  }
}

async function getArtifact(
  artifactId: string
): Promise<ArtifactDetailResponse> {
  try {
    return await client.get(
      `/artifacts/${artifactId}`,
      schema.ArtifactDetailResponse
    );
  } catch (error: unknown) {
    handleApiError(error, `Failed to retrieve artifact '${artifactId}'.`, true);
  }
}

async function getVersions(
  artifactId: string,
  contentType: ArtifactContentType = 'json'
): Promise<ArtifactVersionsResponse> {
  try {
    const query = new URLSearchParams();
    query.set('contentType', contentType);

    return await client.get(
      `/artifacts/${artifactId}/versions?${query.toString()}`,
      schema.ArtifactVersionsResponse
    );
  } catch (error: unknown) {
    handleApiError(
      error,
      `Failed to retrieve versions of artifact '${artifactId}'.`,
      true
    );
  }
}

async function getVersion(versionId: string): Promise<ArtifactVersion> {
  try {
    return await client.get(
      `/artifacts/versions/${versionId}`,
      schema.ArtifactVersion
    );
  } catch (error: unknown) {
    handleApiError(error, `Failed to retrieve version '${versionId}'.`, true);
  }
}

export const artifacts = {
  getAll,
  getArtifact,
  getVersions,
  getVersion
};
